import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import {
  ICreateUpdateTemplateRequest,
  ITemplateEditorParentTemplate,
  ITemplateEditorResponse,
  ITemplateEntity,
} from "../models/TemplateEditor";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";

export const templateEditorQueryKey = ["templateEditor"];

export default function useTemplateEditor() {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const templateEditor = useQuery<ITemplateEditorResponse>({
    queryKey: templateEditorQueryKey,
    queryFn: () => get<ITemplateEditorResponse>("/template/editor"),
    staleTime: 1000 * 60 * 10,
  });

  const invalidate = () => queryClient.invalidateQueries(templateEditorQueryKey);

  const createChildTemplate = useMutation(
    ({ parentTemplateId, request }: { parentTemplateId: string; request: ICreateUpdateTemplateRequest }) =>
      post<ICreateUpdateTemplateRequest, DefaultResponseWithData<ITemplateEntity>>(`/template/${parentTemplateId}/child`, request),
    { onSuccess: invalidate }
  );

  const updateChildTemplate = useMutation(
    ({ childTemplateId, request }: { childTemplateId: string; request: ICreateUpdateTemplateRequest }) =>
      put<ICreateUpdateTemplateRequest>(`/template/child/${childTemplateId}`, request),
    { onSuccess: invalidate }
  );

  const resetChildTemplate = useMutation(
    (childTemplateId: string) =>
      post<{}, DefaultResponseWithData<ITemplateEntity>>(`/template/child/${childTemplateId}/reset`, {}),
    { onSuccess: invalidate }
  );

  const deleteChildTemplate = useMutation(
    (childTemplateId: string) => del<DefaultResponse>(`/template/child/${childTemplateId}`),
    { onSuccess: invalidate }
  );

  const templates = templateEditor.data?.data ?? [];
  const availableParents = templates.filter((template: ITemplateEditorParentTemplate) => !template.childTemplate);

  return {
    templateEditor,
    templates,
    availableParents,
    createChildTemplate,
    updateChildTemplate,
    resetChildTemplate,
    deleteChildTemplate,
  };
}
